/**
 * KnockdownResultsViz - Knockdown simulation results visualization for chat mode.
 *
 * Tabs: Rescue Score | Rescued Genes | Affected Genes | Enriched Pathways
 */

import React, { useState } from 'react';
import RescueScoreBarPlot from '../../visualizations/RescueScoreBarPlot';
import InfoTooltip from './InfoTooltip';

interface KnockdownResultsVizProps {
  data: {
    type: 'knockdown';
    rescue_score?: {
      score: number;
      effectiveness: string;
      pathways?: any[];
    };
    rescued_genes?: any[];
    overall_rescue_distance?: number;
    n_dysregulated?: number;
    n_rescued?: number;
    affected_genes?: any[];
    metadata?: {
      lncrna: string;
      disease: string;
    };
    rescue_distance_chart?: any;
  };
  onAction?: (action: string, params?: any) => void;
}

export const KnockdownResultsViz: React.FC<KnockdownResultsVizProps> = ({ data, onAction }) => {
  const [activeTab, setActiveTab] = useState<'rescue' | 'rescued' | 'genes' | 'pathways'>('rescue');

  const rescueChartData = data.rescue_distance_chart;
  const rescueScore = data.rescue_score;
  const affectedGenes = data.affected_genes || [];
  const enrichrPathways = rescueScore?.pathways || [];

  return (
    <div className="origin-card mt-4 p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-1">
          Knockdown Simulation
          {data.metadata?.lncrna && ` - ${data.metadata.lncrna}`}
        </h3>
        {data.metadata?.disease && (
          <p className="text-sm text-gray-400">{data.metadata.disease}</p>
        )}
      </div>

      {/* In-silico disclaimer */}
      <div className="mb-4 flex items-start gap-2 px-3 py-2 bg-amber-900/30 border border-amber-700 rounded-lg text-xs text-amber-300">
        <span className="mt-0.5 flex-shrink-0">⚠️</span>
        <span>
          <strong>In Silico Simulation.</strong> Results are computational predictions based on gene
          regulatory network models and single-cell transcriptomic data. They do not represent
          experimental validation and should not be used for clinical decision-making.
        </span>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-2 mb-4 border-b border-gray-700 pb-2 flex-wrap">
        <button
          onClick={() => setActiveTab('rescue')}
          className={`origin-btn origin-btn-sm ${activeTab === 'rescue' ? 'origin-btn-primary' : 'origin-btn-ghost'}`}
        >
          🎯 Rescue Score
        </button>
        {data.rescued_genes && data.rescued_genes.length > 0 && (
          <button
            onClick={() => setActiveTab('rescued')}
            className={`origin-btn origin-btn-sm ${activeTab === 'rescued' ? 'origin-btn-primary' : 'origin-btn-ghost'}`}
          >
            ✅ Rescued Genes ({data.rescued_genes.length})
          </button>
        )}
        {affectedGenes.length > 0 && (
          <button
            onClick={() => setActiveTab('genes')}
            className={`origin-btn origin-btn-sm ${activeTab === 'genes' ? 'origin-btn-primary' : 'origin-btn-ghost'}`}
          >
            🧬 Affected Genes ({affectedGenes.length})
          </button>
        )}
        {enrichrPathways.length > 0 && (
          <button
            onClick={() => setActiveTab('pathways')}
            className={`origin-btn origin-btn-sm ${activeTab === 'pathways' ? 'origin-btn-primary' : 'origin-btn-ghost'}`}
          >
            🔬 Pathways ({enrichrPathways.length})
          </button>
        )}
      </div>

      {/* Rescue Score Tab */}
      {activeTab === 'rescue' && (
        <div>
          {rescueScore && (
            <div className="mb-4 p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-1.5 text-sm font-semibold">
                  Rescue Score
                  <InfoTooltip
                    title="Rescue Score (0–1)"
                    content="Fraction of disease dysregulation reversed by knockdown. 1.0 = all dysregulated genes fully restored; 0.0 = no rescue. Weighted by log2FC magnitude so highly dysregulated genes contribute more."
                  />
                </span>
                <span className={`text-2xl font-bold ${
                  rescueScore.effectiveness === 'HIGH' ? 'text-green-400' :
                  rescueScore.effectiveness === 'MEDIUM' ? 'text-yellow-400' :
                  rescueScore.effectiveness === 'LOW' ? 'text-gray-400' :
                  'text-red-400'
                }`}>
                  {(rescueScore.score / 100).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                Effectiveness: <span className="font-semibold">{rescueScore.effectiveness}</span>
                <InfoTooltip
                  title="Effectiveness Level"
                  content="HIGH ≥ 0.60 | MEDIUM 0.30–0.60 | LOW 0.10–0.30 | NONE < 0.10"
                />
              </div>
              <div className="text-xs text-gray-500 mt-1">
                0–1 scale · weighted by log2FC magnitude · fraction of dysregulation reversed
              </div>
            </div>
          )}

          {rescueChartData && (
            <RescueScoreBarPlot
              data={{
                target_gene: data.metadata?.lncrna || 'Unknown',
                rescue_score: rescueScore?.score || 0,
                effectiveness: rescueScore?.effectiveness === 'HIGH' ? 'Highly Effective' :
                               rescueScore?.effectiveness === 'MEDIUM' ? 'Moderately Effective' :
                               rescueScore?.effectiveness === 'LOW' ? 'Low Effectiveness' : 'Not Effective',
                top_rescued_pathways: [],
                baseline_score: rescueChartData.baseline_score,
                knockdown_score: rescueChartData.knockdown_score
              }}
              title="Rescue Score Analysis"
            />
          )}

          {/* Overall Rescue Distance */}
          {data.overall_rescue_distance !== undefined && (
            <div className="mt-4 p-3 bg-green-900/30 border border-green-800 rounded-lg">
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
                Overall Rescue Distance
                <InfoTooltip
                  title="Overall Rescue Distance"
                  content="Percentage reduction in expression-state distance from disease toward normal after knockdown. Measures how much the entire gene expression profile shifted back toward a healthy state."
                  position="right"
                />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-green-400">
                  {data.overall_rescue_distance.toFixed(1)}%
                </span>
                <span className="text-xs text-gray-500">reduction in distance to normal state</span>
              </div>
              {data.n_rescued !== undefined && data.n_dysregulated !== undefined && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                  {data.n_rescued} of {data.n_dysregulated} dysregulated genes rescued
                  <InfoTooltip
                    title="Rescued vs Dysregulated"
                    content="Rescued: genes that moved toward normal expression levels after knockdown. Dysregulated: total number of genes abnormally expressed in this disease."
                    position="right"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Rescued Genes Tab */}
      {activeTab === 'rescued' && data.rescued_genes && data.rescued_genes.length > 0 && (
        <div className="max-h-[400px] overflow-auto">
          <div className="mb-3 p-3 bg-green-900/30 rounded-lg">
            <p className="text-sm text-gray-300">
              Genes dysregulated in disease that moved toward normal expression after knockdown.
              Sorted by weighted contribution (improvement × |log2FC|).
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900 border-b-2 border-gray-600">
              <tr>
                <th className="text-left py-2 px-3">Gene</th>
                <th className="text-right py-2 px-3">
                  <span className="flex items-center justify-end gap-1">
                    Improvement
                    <InfoTooltip title="Improvement" content="Percentage of this gene's dysregulation corrected by knockdown. 100% = fully restored to normal." position="right" />
                  </span>
                </th>
                <th className="text-right py-2 px-3">
                  <span className="flex items-center justify-end gap-1">
                    |log2FC|
                    <InfoTooltip title="Absolute Log2 Fold-Change" content="Absolute magnitude of dysregulation in disease. Used to weight the rescue score — highly dysregulated genes contribute more." position="right" />
                  </span>
                </th>
                <th className="text-left py-2 px-3">Direction</th>
              </tr>
            </thead>
            <tbody>
              {data.rescued_genes.map((gene: any, idx: number) => (
                <tr key={idx} className="border-b border-gray-700 hover:bg-green-900/20 transition-colors">
                  <td className="py-2 px-3 font-mono text-xs font-semibold">{gene.gene}</td>
                  <td className="py-2 px-3 text-right">
                    <span className="text-green-400 font-bold">
                      {gene.improvement?.toFixed(1) || 0}%
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-xs text-gray-400">
                    {gene.log2fc_magnitude?.toFixed(2) ?? gene.original_dysregulation?.toFixed(2) ?? '—'}
                  </td>
                  <td className="py-2 px-3">
                    <span className={`text-xs px-2 py-1 rounded ${
                      gene.direction === 'up' ? 'bg-red-900/30 text-red-400' : 'bg-blue-900/30 text-blue-400'
                    }`}>
                      {gene.direction === 'up' ? '⬆ Up' : '⬇ Down'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Affected Genes Tab */}
      {activeTab === 'genes' && affectedGenes.length > 0 && (
        <div className="max-h-[400px] overflow-auto">
          <div className="mb-3 p-3 bg-blue-900/30 rounded-lg">
            <p className="text-sm text-gray-300">
              Genes whose expression changed after knockdown propagation through the GRN.
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900 border-b border-gray-700">
              <tr>
                <th className="text-left py-2 px-3">Gene</th>
                <th className="text-right py-2 px-3">Expression Change</th>
                <th className="text-left py-2 px-3">Direction</th>
              </tr>
            </thead>
            <tbody>
              {affectedGenes.map((gene: any, idx: number) => {
                const change = gene.mean_change ?? gene.expression_change ?? 0;
                return (
                  <tr key={idx} className="border-b border-gray-700 hover:bg-gray-800">
                    <td className="py-2 px-3 font-mono text-xs">{gene.gene}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {Math.abs(change).toFixed(4)}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-xs ${change > 0 ? 'text-red-400' : 'text-blue-400'}`}>
                        {change > 0 ? '↑ Increased' : '↓ Decreased'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Enriched Pathways Tab (real Enrichr data) */}
      {activeTab === 'pathways' && enrichrPathways.length > 0 && (
        <div className="max-h-[400px] overflow-auto">
          <div className="mb-3 p-3 bg-purple-950/30 rounded-lg">
            <p className="text-sm text-gray-300">
              Pathways enriched in genes affected by knockdown (Enrichr API).
            </p>
          </div>
          <div className="space-y-3">
            {enrichrPathways.map((pathway: any, idx: number) => {
              const name = pathway.name || pathway.pathway || pathway.term || 'Unknown';
              const score = pathway.impact_score ?? pathway.rescue_percentage ?? 0;
              const pval = pathway.adj_p_value ?? pathway.p_value;
              const db = pathway.database || '';
              const maxScore = Math.max(...enrichrPathways.map((p: any) => p.impact_score ?? p.rescue_percentage ?? 0), 1);
              return (
                <div key={idx}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-300 font-medium truncate max-w-[70%]" title={name}>
                      {name}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      {pval !== undefined && (
                        <span className="flex items-center gap-0.5 text-gray-400">
                          p={pval < 0.001 ? pval.toExponential(1) : pval.toFixed(3)}
                          <InfoTooltip title="P-value" content="FDR-adjusted p-value from Enrichr. Values < 0.05 are statistically significant." position="right" />
                        </span>
                      )}
                      {db && <span className="text-gray-400 text-[10px]">{db}</span>}
                      <span className="flex items-center gap-0.5 text-purple-400 font-semibold">
                        {score.toFixed(1)}
                        <InfoTooltip title="Impact Score" content="Proportion of the overall rescue effect attributable to this pathway. Higher = this pathway's genes were more affected by the knockdown." position="right" />
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (score / maxScore) * 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default KnockdownResultsViz;
